import { Component, EventEmitter, Input, Output, OnInit, signal } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { GraphAnnotationType, GraphAnnotationModel, PatientModel } from '../../../core/api/models';
import { GraphApiService, StaffApiService, SupervisorApiService } from '../../../core/api/services';
import { Router } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { TEXT_POSITION_OPTIONS, TextPositionOption } from '../../../shared/utils/utils';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-add-annotation',
  templateUrl: './add-annotation.component.html',
  styleUrl: './add-annotation.component.css',
  standalone: false,
})
export class AddAnnotationComponent implements OnInit {
  @Input() patientID: number | null = null;
  @Input() annotationToEdit: GraphAnnotationModel | null = null;
  @Output() close = new EventEmitter<boolean>();

  isLoading = signal<boolean>(false);

  annotationTypes: any[] = [
    { value: GraphAnnotationType.YAxis, text: 'Y Axis', tooltip: 'This annotation will appear as a horizontal line on the graph.' },
    { value: GraphAnnotationType.XAxis, text: 'X Axis', tooltip: 'This annotation will appear as a vertical line on the graph.' },
    { value: GraphAnnotationType.YAxisRange, text: 'Y Axis Range', tooltip: 'This annotation will appear as a horizontal range on the graph.' },
    { value: GraphAnnotationType.XAxisRange, text: 'X Axis Range', tooltip: 'This annotation will appear as a vertical range on the graph.' },
    { value: GraphAnnotationType.Point, text: 'Point', tooltip: 'This annotation will appear as a single point on the graph.' },
    { value: GraphAnnotationType.Text, text: 'Text', tooltip: 'This annotation will appear as a text label on the graph.' },
    // TODO: Add image annotation type later if needed.
    // { value: GraphAnnotationType.Image, text: 'Image' },
  ];

  annotationForm = new FormGroup({
    label: new FormControl('', [Validators.required, Validators.maxLength(100), Validators.minLength(1)]),
    type: new FormControl<GraphAnnotationType>(GraphAnnotationType.YAxis),
    yAxisMin: new FormControl<number | null>(null, [this.yAxisMinValidator()]),
    yAxisMax: new FormControl<number | null>(null, [this.yAxisMaxValidator()]),
    startDate: new FormControl<string>(new Date().toISOString(), [this.startDateValidator()]),
    endDate: new FormControl<string | null>(null, [this.endDateValidator()]),
    color: new FormControl<string>('#000000'),
    notes: new FormControl(''),
    isPrivate: new FormControl(false),
    visibleForUser: new FormControl(true),
    shouldBreakSeries: new FormControl(false),
    selectedTextPosition: new FormControl<string | null>(null),
    labelFontSize: new FormControl<number | null>(14, [Validators.min(8), Validators.max(72)]),
    selections: new FormGroup({
      targetIDs: new FormControl<number[]>([]),
      behaviorIDs: new FormControl<number[]>([]),
      programIDs: new FormControl<number[]>([]),
      skillAreaIDs: new FormControl<number[]>([]),
    }, { validators: this.atLeastOneSelectionValidator() })
  });

  titleName: string = 'Add Annotation';
  actionName: string = 'Add';

  selectableTargets: SelectItem[] = [];
  selectableBehaviors: SelectItem[] = [];
  selectablePrograms: SelectItem[] = [];
  selectableSkillAreas: SelectItem[] = [];

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private router: Router,
    private alertService: AlertService,
    private patientDataFacade: PatientDataFacadeService
  ) {

    this.annotationForm.controls.type.valueChanges.subscribe((value) => {
      console.log('type', value);
      // refresh the form validation rules based on the type
      this.annotationForm.controls.yAxisMin.updateValueAndValidity();
      this.annotationForm.controls.yAxisMax.updateValueAndValidity();
      this.annotationForm.controls.startDate.updateValueAndValidity();
      this.annotationForm.controls.endDate.updateValueAndValidity();
      
      // When switching to/from Point type, convert date format if needed
      const currentStartDate = this.annotationForm.controls.startDate.value;
      if (currentStartDate) {
        if (value === GraphAnnotationType.Point) {
          // Convert datetime-local to date format (YYYY-MM-DD)
          const date = new Date(currentStartDate);
          const dateOnly = date.toISOString().split('T')[0];
          this.annotationForm.controls.startDate.setValue(dateOnly, { emitEvent: false });
        } else if (this.annotationForm.controls.startDate.value && this.annotationForm.controls.startDate.value.length === 10) {
          // If switching from Point (date format) to another type, convert to datetime-local
          const dateOnly = this.annotationForm.controls.startDate.value;
          const dateTime = new Date(dateOnly + 'T00:00:00').toISOString().slice(0, 16);
          this.annotationForm.controls.startDate.setValue(dateTime, { emitEvent: false });
        }
      }
      
      // When switching to/from Text type, reset position dropdown
      if (value === GraphAnnotationType.Text) {
        // Try to find matching position for current coordinates
        const currentX = this.annotationForm.controls.yAxisMin.value;
        const currentY = this.annotationForm.controls.yAxisMax.value;
        if (currentX != null && currentY != null) {
          const matchingPosition = this.textPositionOptions.find(
            opt => opt.x === currentX && opt.y === currentY
          );
          if (matchingPosition) {
            this.annotationForm.controls.selectedTextPosition.setValue(matchingPosition.value, { emitEvent: false });
          } else {
            this.annotationForm.controls.selectedTextPosition.setValue(null, { emitEvent: false });
          }
        } else {
          this.annotationForm.controls.selectedTextPosition.setValue(null, { emitEvent: false });
        }
      } else {
        this.annotationForm.controls.selectedTextPosition.setValue(null, { emitEvent: false });
      }
      
      // Force change detection for the template
      this.annotationForm.updateValueAndValidity();
    });

    // Add cross-validation for date fields
    this.annotationForm.controls.startDate.valueChanges.subscribe(() => {
      this.annotationForm.controls.endDate.updateValueAndValidity();
    });

    this.annotationForm.controls.endDate.valueChanges.subscribe(() => {
      this.annotationForm.controls.startDate.updateValueAndValidity();
    });

    // Add cross-validation for Y-axis fields
    this.annotationForm.controls.yAxisMin.valueChanges.subscribe(() => {
      this.annotationForm.controls.yAxisMax.updateValueAndValidity();
    });

    this.annotationForm.controls.yAxisMax.valueChanges.subscribe(() => {
      this.annotationForm.controls.yAxisMin.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    // Load data for multi-selects
    this.loadData();
  }

  loadData() {
    this.isLoading.set(true);
    forkJoin({
      targets: this.patientDataFacade.getTargets(this.patientID ?? 0, {
        onlyActive: false,
      }),
      behaviors: this.staffApiService.GetPatientBehaviors({ patientID: this.patientID ?? 0 }),
      programs: this.patientDataFacade.getPrograms(this.patientID ?? 0, {
        onlyActive: false,
      }),
      skillAreas: this.supervisorApiService.GetSkillAreas({ patientID: this.patientID ?? 0 })
    }).subscribe({
      next: (result) => {
        this.selectableTargets = result.targets.filter((target) => !target.IsDeleted).map((target) => ({
          label: target.TargetName ?? '',
          value: target.ID,
        }));

        this.selectableBehaviors = result.behaviors.filter((behavior) => !behavior.IsDeleted).map((behavior) => ({
          label: behavior.PatientBehaviorName ?? '',
          value: behavior.ID,
        }));

        this.selectablePrograms = result.programs.filter((program) => !program.IsDeleted).map((program) => ({
          label: program.ProgramName ?? '',
          value: program.ID,
        }));

        this.selectableSkillAreas = result.skillAreas.filter((skillArea) => !skillArea.IsDeleted).map((skillArea) => ({
          label: skillArea.SkillAreaName ?? '',
          value: skillArea.ID,
        }));
      },
      complete: () => {
        this.isLoading.set(false);

        // EDIT LOGIC
        if (this.annotationToEdit != null) {
          this.titleName = 'Edit Annotation';
          this.actionName = 'Save';

          const annotationType = this.annotationToEdit?.GraphAnnotationType ?? GraphAnnotationType.YAxis;
          let startDateValue = this.annotationToEdit?.StartDate ?? new Date().toISOString();
          
          // Convert ISO datetime to date-only format for Point type
          if (annotationType === GraphAnnotationType.Point && startDateValue) {
            const date = new Date(startDateValue);
            startDateValue = date.toISOString().split('T')[0]; // YYYY-MM-DD format
          } else if (startDateValue && startDateValue.length > 10) {
            // For other types, convert to datetime-local format (YYYY-MM-DDTHH:mm)
            const date = new Date(startDateValue);
            startDateValue = date.toISOString().slice(0, 16);
          }
          
          const yAxisMinValue = this.annotationToEdit?.YValueStart ?? null;
          const yAxisMaxValue = this.annotationToEdit?.YValueEnd ?? null;
          
          // For Text type, try to find matching position preset
          let selectedPositionValue: string | null = null;
          if (annotationType === GraphAnnotationType.Text && yAxisMinValue != null && yAxisMaxValue != null) {
            const matchingPosition = this.textPositionOptions.find(
              opt => opt.x === yAxisMinValue && opt.y === yAxisMaxValue
            );
            if (matchingPosition) {
              selectedPositionValue = matchingPosition.value;
            }
          }
          
          this.annotationForm.patchValue({
            label: this.annotationToEdit?.Label ?? '',
            type: annotationType,
            yAxisMin: yAxisMinValue,
            yAxisMax: yAxisMaxValue,
            startDate: startDateValue,
            endDate: this.annotationToEdit?.EndDate ?? null,
            color: this.annotationToEdit?.Color ?? '#000000',
            notes: this.annotationToEdit?.Notes ?? '',
            isPrivate: this.annotationToEdit?.IsPrivate ?? false,
            visibleForUser: this.annotationToEdit?.VisibleForUser ?? true,
            shouldBreakSeries: this.annotationToEdit?.ShouldBreakSeries ?? false,
            labelFontSize: this.annotationToEdit?.LabelFontSize ?? 14,
            selections: {
              targetIDs: this.annotationToEdit?.PatientTargetIDs ?? [],
              behaviorIDs: this.annotationToEdit?.PatientBehaviorIDs ?? [], 
              programIDs: this.annotationToEdit?.PatientProgramIDs ?? [],
              skillAreaIDs: this.annotationToEdit?.PatientSkillAreaIDs ?? [],
            }
          });
          
          // Set the selected position for Text type
          if (annotationType === GraphAnnotationType.Text) {
            this.annotationForm.controls.selectedTextPosition.setValue(selectedPositionValue, { emitEvent: false });
          }
        }
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Error loading data for multi-selects');
        this.isLoading.set(false);
      }
    });
  }

  // Custom validator function
  private atLeastOneSelectionValidator(): ValidatorFn {
    return (group: AbstractControl): {[key: string]: any} | null => {
      const targetIDs = group.get('targetIDs')?.value || [];
      const behaviorIDs = group.get('behaviorIDs')?.value || [];
      const programIDs = group.get('programIDs')?.value || [];
      const skillAreaIDs = group.get('skillAreaIDs')?.value || [];

      const hasSelection = 
        targetIDs.length > 0 || 
        behaviorIDs.length > 0 || 
        programIDs.length > 0 || 
        skillAreaIDs.length > 0;

      return hasSelection ? null : { noSelection: true };
    };
  }

  // Helper method to check if selections are valid
  get hasSelectionError(): boolean {
    const selectionsGroup = this.annotationForm.get('selections');
    return selectionsGroup?.errors?.['noSelection'] && selectionsGroup.touched;
  }

  private yAxisMinValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.annotationForm != null) {
        const currentType = this.annotationForm.controls.type.value;
        
        // if type is point, checks only if value exists
        if (currentType === GraphAnnotationType.Point) {
          if (control.value == null) {
            return { yValueStartRequired: true };
          }
          return null;
        }

        // if type is text, checks if position is selected (which sets both x and y)
        if (currentType === GraphAnnotationType.Text) {
          const selectedPosition = this.annotationForm.controls.selectedTextPosition.value;
          if (selectedPosition == null || selectedPosition === '') {
            return { yAxisMinRequired: true };
          }
          return null;
        }

        if (currentType !== GraphAnnotationType.YAxisRange && currentType !== GraphAnnotationType.YAxis) {
          return null;
        } 

        // if type is yaxis, checks only if value exists
        if (currentType === GraphAnnotationType.YAxis) {
          if (control.value == null) {
            return { yAxisMinRequired: true };
          }
        } 

        // if type is yaxisrange, checks if value exists and if it is less than yaxismax
        if (currentType === GraphAnnotationType.YAxisRange) {
          if (control.value == null) {
            return { yAxisMinRequired: true };
          }

          const yAxisMin = control.value;
          const yAxisMax = this.annotationForm.get('yAxisMax')?.value;

          if (yAxisMin != null && yAxisMax != null && yAxisMin > yAxisMax) {
            return { yAxisMinGreaterThanMax: true };
          }
        }
      }

      return null;
    }
  }

  private yAxisMaxValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.annotationForm != null) {
        const currentType = this.annotationForm.controls.type.value;
        
        // if type is text, checks if position is selected (which sets both x and y)
        if (currentType === GraphAnnotationType.Text) {
          const selectedPosition = this.annotationForm.controls.selectedTextPosition.value;
          if (selectedPosition == null || selectedPosition === '') {
            return { yAxisMaxRequired: true };
          }
          return null;
        }
        
        if (currentType !== GraphAnnotationType.YAxisRange) {
          return null;
        }
        
        // if type is yaxisrange, checks if value exists and if it is greater than yaxismin
        if (currentType === GraphAnnotationType.YAxisRange) {
          if (control.value == null) {
            return { yAxisMaxRequired: true };
          }
         
          const yAxisMax = control.value;
          const yAxisMin = this.annotationForm.get('yAxisMin')?.value;

          if (yAxisMax != null && yAxisMin != null && yAxisMax < yAxisMin) {
            return { yAxisMaxLessThanMin: true };
          }
        }
      }

      return null;
    }
  }

  private startDateValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.annotationForm != null) {
        const currentType = this.annotationForm.controls.type.value;
        
        // if type is point, checks only if value exists
        if (currentType === GraphAnnotationType.Point) {
          if (control.value == null) {
            return { startDateRequired: true };
          }
          return null;
        }

        // if type is text, no date validation needed
        if (currentType === GraphAnnotationType.Text) {
          return null;
        }

        if (currentType !== GraphAnnotationType.XAxisRange && currentType !== GraphAnnotationType.XAxis) {
          return null;
        }

        // if type is xaxis, checks only if value exists
        if (currentType === GraphAnnotationType.XAxis) {
          if (control.value == null) {
            return { startDateRequired: true };
          }
        }

        // if type is xaxisrange, checks if value exists and if it is greater than startdate
        if (currentType === GraphAnnotationType.XAxisRange) {
          if (control.value == null) {
            return { startDateRequired: true };
          }

          const startDate = control.value;
          const endDate = this.annotationForm.get('endDate')?.value;

          if (startDate != null && endDate != null && startDate > endDate) {
            return { startDateGreaterThanEndDate: true };
          }
        }
      }

      return null;
    }
  }

  private endDateValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.annotationForm != null) {
        if (this.annotationForm.controls.type.value !== GraphAnnotationType.XAxisRange) {
          return null;
        }

        // if type is xaxisrange, checks if value exists and if it is less than startdate
        if (this.annotationForm.controls.type.value === GraphAnnotationType.XAxisRange) {
          if (control.value == null) {
            return { endDateRequired: true };
          }
          
          const endDate = control.value;
          const startDate = this.annotationForm.get('startDate')?.value;

          if (endDate != null && startDate != null && endDate < startDate) {
            return { endDateLessThanStartDate: true };
          }
        }
      }

      return null;
    }
  }

  onSubmit() {
    console.log(this.annotationForm.value);

    this.annotationForm.markAllAsTouched();

    if (this.annotationForm.invalid) {
      const errors = Utils.getFormErrors(this.annotationForm);
      if (errors.length > 0) {
        this.alertService.error(errors.join(' \n'));
        return;
      }
    }

    this.saveAnnotation();
  }

  saveAnnotation() {
    var formValue = this.annotationForm.getRawValue();
    
    // Convert date-only format (from Point type) to ISO datetime string for API
    let startDate = formValue.startDate;
    if (formValue.type === GraphAnnotationType.Point && startDate && startDate.length === 10) {
      // Date is in YYYY-MM-DD format, convert to ISO datetime (start of day)
      startDate = new Date(startDate + 'T00:00:00').toISOString();
    }
    
    this.annotationToEdit = {
      ...this.annotationToEdit,
      ID: this.annotationToEdit?.ID,
      PatientID: this.patientID ?? 0,
      Label: formValue.label,
      GraphAnnotationType: formValue.type ?? GraphAnnotationType.YAxis,
      YValueStart: formValue.yAxisMin,
      YValueEnd: formValue.yAxisMax,
      StartDate: startDate,
      EndDate: formValue.endDate,
      Color: formValue.color,
      Notes: formValue.notes,
      IsPrivate: formValue.isPrivate ?? false,
      VisibleForUser: formValue.visibleForUser ?? true,
      ShouldBreakSeries: formValue.shouldBreakSeries ?? false,
      LabelFontSize: formValue.labelFontSize ?? 14,
      LabelPosition: this.annotationToEdit?.LabelPosition ?? null,
      PatientTargetIDs: formValue.selections.targetIDs,
      PatientBehaviorIDs: formValue.selections.behaviorIDs,
      PatientProgramIDs: formValue.selections.programIDs,
      PatientSkillAreaIDs: formValue.selections.skillAreaIDs,
    };

    this.graphApiService.SaveGraphAnnotation({
      body: this.annotationToEdit
    }).subscribe({
      next: () => {
        this.alertService.success('Annotation saved successfully');
        this.close.emit(true);
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Error saving annotation - ' + Utils.getErrorMessageFromErrorResponse(error));
        this.close.emit(false);
      }
    });
  }
  
  onCancel() {
    this.close.emit(false);
  }

  /**
   * Returns true if the current annotation type is X-Axis, which is the only type that supports breaking series
   */
  shouldShowBreakSeriesOption(): boolean {
    const currentType = this.annotationForm.get('type')?.value;
    return currentType === GraphAnnotationType.XAxis;
  }

  needsYAxisMin(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.YAxisRange || 
           this.annotationForm.value.type === GraphAnnotationType.YAxis ||
           this.annotationForm.value.type === GraphAnnotationType.Text;
  }

  needsYAxisMax(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.YAxisRange ||
           this.annotationForm.value.type === GraphAnnotationType.Text;
  }

  needsStartDate(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.XAxisRange || this.annotationForm.value.type === GraphAnnotationType.XAxis || this.annotationForm.value.type === GraphAnnotationType.Point;
  }

  needsEndDate(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.XAxisRange;
  }

  isPointType(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.Point;
  }

  needsYValueStartForPoint(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.Point;
  }

  isTextType(): boolean {
    return this.annotationForm.value.type === GraphAnnotationType.Text;
  }

  textPositionOptions: TextPositionOption[] = TEXT_POSITION_OPTIONS;

  onTextPositionChange(): void {
    const selectedPositionValue = this.annotationForm.controls.selectedTextPosition.value;
    const selectedPosition = this.textPositionOptions.find(
      opt => opt.value === selectedPositionValue
    );
    if (selectedPosition) {
      this.annotationForm.controls.yAxisMin.setValue(selectedPosition.x, { emitEvent: false });
      this.annotationForm.controls.yAxisMax.setValue(selectedPosition.y, { emitEvent: false });
      // Trigger validation
      this.annotationForm.controls.yAxisMin.updateValueAndValidity({ emitEvent: false });
      this.annotationForm.controls.yAxisMax.updateValueAndValidity({ emitEvent: false });
    }
  }

  tooltipText(): string {
    return this.annotationTypes.find(type => type.value === this.annotationForm.value.type)?.tooltip ?? '';
  }

}
